import { useState, useEffect, useRef } from "react";
import { Palette, Calendar, Bell, MapPin, Radio, BarChart3 } from "lucide-react";

interface Feature {
  icon: React.ElementType;
  title: string;
  description: string;
}

const features: Feature[] = [
  {
    icon: Palette,
    title: "Créez des cartes professionnelles",
    description: "Pas besoin de compétences en design. Notre éditeur visuel vous permet de personnaliser logos, couleurs, images et textes. L'aperçu en temps réel vous montre exactement le rendu sur mobile. Résultat professionnel garanti."
  },
  {
    icon: Calendar,
    title: "Programmez vos messages à l'avance",
    description: "Planifiez vos communications importantes plusieurs jours ou semaines en avance. Vos annonces partent automatiquement au moment optimal, sans intervention de votre part. Vous gardez le contrôle de votre calendrier éditorial."
  },
  {
    icon: Bell,
    title: "Envoyez des notifications impossibles à manquer",
    description: "Diffusez vos messages urgents directement sur l'écran verrouillé de tous vos abonnés. C'est le moyen le plus efficace de couper à travers le bruit des emails et réseaux sociaux pour déclencher une action immédiate."
  },
  {
    icon: MapPin,
    title: "Déclenchez des alertes géolocalisées automatiques",
    description: "Touchez vos abonnés au bon endroit, au bon moment. Envoyez automatiquement une notification quand quelqu'un entre dans une zone définie : votre boutique, un salon professionnel, un événement local. Parfait pour ramener du monde et délivrer des messages pertinents."
  },
  {
    icon: Radio,
    title: "Construisez votre propre canal de communication",
    description: "Créez un lien direct avec votre audience, sans dépendre des algorithmes ou des plateformes. La carte Wallet établit une connexion directe avec vos abonnés. Vous décidez de ce qu'ils voient, quand ils le voient, et à quelle fréquence."
  },
  {
    icon: BarChart3,
    title: "Suivez vos performances en un coup d'œil",
    description: "Visualisez la croissance de votre audience facilement. Abonnés, notifications envoyées, clics sur les liens : tout visible d'un coup d'œil. Vous mesurez votre portée et l'efficacité de vos campagnes sans complications."
  }
];

const FeatureCard = ({ feature, index, isVisible, animationComplete }: { feature: Feature; index: number; isVisible: boolean; animationComplete: boolean }) => {
  const Icon = feature.icon;
  
  return (
    <div 
      className={`group p-6 sm:p-8 rounded-2xl bg-white border border-gray-100 
        transition-[transform,border-color,box-shadow] duration-150 ease-out
        hover:-translate-y-2 hover:border-[#0C94A6]/20
        shadow-[0_4px_20px_-4px_rgba(0,0,0,0.08),0_2px_8px_-2px_rgba(0,0,0,0.04)]
        hover:shadow-[0_12px_32px_-8px_rgba(0,0,0,0.12),0_4px_16px_-4px_rgba(0,0,0,0.08)]
        ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`}
      style={{
        transitionDelay: !animationComplete && isVisible ? `${index * 100}ms` : '0ms'
      }}
    >
      {/* Icône dans cercle */}
      <div className="w-12 h-12 rounded-full bg-[#0C94A6]/15 flex items-center justify-center mb-4">
        <Icon className="w-6 h-6 text-[#0C94A6]" />
      </div>
      
      {/* Titre de la feature */}
      <h3 className="text-lg sm:text-xl font-bold text-[#022226] mb-3">
        {feature.title}
      </h3>
      
      {/* Texte descriptif - MÊME style que paragraphe intro */}
      <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
        {feature.description}
      </p>
    </div>
  );
};

export const Features = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const [animationComplete, setAnimationComplete] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Supprimer le transitionDelay après l'animation d'entrée
  useEffect(() => {
    if (isVisible && !animationComplete) {
      const timer = setTimeout(() => {
        setAnimationComplete(true);
      }, 700);
      return () => clearTimeout(timer);
    }
  }, [isVisible, animationComplete]);

  useEffect(() => {
    if (!isMounted) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (contentRef.current) {
      observer.observe(contentRef.current);
    }

    return () => observer.disconnect();
  }, [isMounted]);

  return (
    <section id="fonctionnalites" className="pt-[50px] sm:pt-[62.5px] lg:pt-[75px] pb-[50px] sm:pb-[62.5px] lg:pb-[70px] bg-[#FAFBFB] scroll-mt-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div
          ref={contentRef}
          className={`transition-all duration-700 ease-out ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
          }`}
        >
          {/* Header de section - Copie exacte des styles existants */}
          <div className="text-center mb-12 sm:mb-16">
            {/* Badge - même composant */}
            <div className="flex justify-center mb-6">
              <span className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#074155] bg-white text-sm font-semibold text-[#074155]">
                Fonctionnalités
              </span>
            </div>

            {/* Titre H2 - même composant */}
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-4">
              Notifications Wallet avec{" "}
              <span className="text-[#0C94A6]">géolocalisation intelligente</span>
            </h2>

            {/* Paragraphe intro - même composant */}
            <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456] max-w-3xl mx-auto">
              <span className="text-[#0C94A6] font-semibold">Unotif</span> regroupe toutes les fonctionnalités dont vous avez besoin pour communiquer efficacement, dans une interface simple et claire.
            </p>
          </div>

          {/* Grille de 6 cartes */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
            {features.map((feature, index) => (
              <FeatureCard 
                key={index} 
                feature={feature} 
                index={index} 
                isVisible={isVisible}
                animationComplete={animationComplete}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
